/**
 * Centralized PostHog event name constants.
 * Use these instead of raw strings to prevent typos and enable autocomplete.
 */
import { OPERATOR_TELEMETRY_EVENTS } from "@brev-io/common/src/utils/operator-turn-telemetry";

/**
 * Every invite surface, and the only definition of the BRE-5497 `source`
 * taxonomy. It is a TYPE rather than a doc comment because the comment alone
 * did not hold: `InviteTeamDialog` documented `"onboarding_tour"` and
 * `"sidebar_button"`, neither of which was ever a real bucket, and following
 * that example would have minted a fifth one silently.
 */
export type InviteSource =
  | "teammate_management"
  | "invite_team_dialog"
  | "ai_summary_placeholder"
  | "nav_sidebar"
  // BRE-6351 - the invite link in the guided flow's complete close. A LINK
  // there, not an ask: the closing beat carries zero of them.
  | "onboarding_closing"
  // BRE-7598 - the Operator's confirmation card (MemberProposalCard), the
  // third path to `createOrgInvitation`. No `_ui_shown`/`_ui_opened` bucket:
  // see the comment on that component for why.
  | "operator_invite_card"
  // BRE-8803 - the "No activity yet" state on Pulse.
  | "pulse_empty";

export const ANALYTICS_EVENTS = Object.freeze({
  // ── Auth ──────────────────────────────────────────────────────────────
  AUTH_SESSION_START: "auth_session_start",
  AUTH_LOGIN: "auth_login",
  AUTH_LOGIN_FAILED: "auth_login_failed",
  AUTH_LOGOUT: "auth_logout",
  AUTH_SIGNUP: "auth_signup",
  AUTH_SIGNUP_FAILED: "auth_signup_failed",
  /**
   * User attempted to sign up with an email that already has an account.
   * This is a distinct UX outcome — not a genuine signup failure — so it
   * is tracked separately from AUTH_SIGNUP_FAILED to keep error dashboards
   * focused on real problems (rate limits, validation, network, etc.) and
   * to enable funnel analysis of "tried signup → account exists → login".
   */
  AUTH_SIGNUP_EXISTING_ACCOUNT: "auth_signup_existing_account",
  AUTH_SIGNUP_COMPLETED: "auth_signup_completed",
  AUTH_MAGIC_CODE_SENT: "auth_magic_code_sent",
  AUTH_MAGIC_CODE_FAILED: "auth_magic_code_failed",
  /**
   * BRE-5732. Authentication could not be DECIDED — a defect or an outage,
   * not a logout. Deliberately not folded into AUTH_REDIRECT's `no_session`
   * reason: the whole bug this event exists for was a framework misuse that
   * presented as a routine session expiry, so the two must never share a
   * signal. Properties: `reason` (post_response_request_scope | unexpected)
   * and `error_name`. Volume here should be zero; treat any as an alert.
   */
  AUTH_UNDECIDED: "auth_undecided",
  AUTH_SSO_REDIRECT: "auth_sso_redirect",
  AUTH_GOOGLE_OAUTH_CLICKED: "auth_google_oauth_clicked",
  AUTH_MICROSOFT_OAUTH_CLICKED: "auth_microsoft_oauth_clicked",
  AUTH_SLACK_OAUTH_CLICKED: "auth_slack_oauth_clicked",
  /**
   * Fired when the in-app/embedded-browser notice is shown on signup/login in
   * place of the social-OAuth buttons. Google's "Use secure browsers" policy
   * blocks its OAuth consent screen inside webviews (LinkedIn, Gmail, Slack,
   * Instagram, Messenger, LINE, …), so we hide the dead Google button and steer
   * the user to email/magic-code. Properties: `app_source` (detected app name
   * or null) and `platform` (ios | android | other) so we can size the volume
   * and see which apps drive it. (BRE-4437.)
   */
  AUTH_IN_APP_BROWSER_DETECTED: "auth_in_app_browser_detected",
  /**
   * Fired when a user taps an escape hatch on the in-app-browser notice —
   * "Open in browser" (Android intent) or "Copy link". Separate from
   * AUTH_IN_APP_BROWSER_DETECTED (which only says the notice was shown) so we
   * can measure how many of those users actually try to leave the webview vs.
   * fall back to email. Properties: `method` (open_in_browser | copy_link),
   * `app_source`, `platform`. (BRE-4437.)
   */
  AUTH_IN_APP_BROWSER_ESCAPE_CLICKED: "auth_in_app_browser_escape_clicked",
  AUTH_MFA_CHALLENGED: "auth_mfa_challenged",
  AUTH_MFA_VERIFIED: "auth_mfa_verified",
  AUTH_MFA_FAILED: "auth_mfa_failed",
  AUTH_EMAIL_VERIFICATION_REQUIRED: "auth_email_verification_required",
  AUTH_EMAIL_VERIFIED: "auth_email_verified",
  AUTH_EMAIL_VERIFY_FAILED: "auth_email_verify_failed",
  /**
   * BRE-5496: a server-side layout/page bounced an authenticated (or
   * half-authenticated) request away from the app - e.g. the authenticated
   * layout redirecting to /login because auth() threw, or to /provisioning
   * because the internal user row is missing. Mirrors the long-standing
   * `auth_redirect` CloudWatch logger events, which were invisible in
   * PostHog: users who signed up, hit one of these bounces repeatedly, and
   * left showed zero client events and no funnel signal. Captured
   * server-side (captureServerEvent) with properties `from`, `to`, `reason`
   * and the user's uuid as distinctId when known ("unauthenticated"
   * otherwise). Keep the logger calls alongside - CloudWatch retains the
   * error object detail PostHog doesn't need.
   */
  AUTH_REDIRECT: "auth_redirect",

  // ── Onboarding (funnel + diagnostics) ──────────────────────────────────
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_STEP_VIEWED: "onboarding_step_viewed",
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  ONBOARDING_STEP_LOAD_TIME: "onboarding_step_load_time",
  ONBOARDING_COMPLETED: "onboarding_completed",
  ONBOARDING_ABANDONED: "onboarding_abandoned",
  ONBOARDING_BACK_CLICKED: "onboarding_back_clicked",
  ONBOARDING_CONTINUE_CLICKED: "onboarding_continue_clicked",
  ONBOARDING_SAVE_FAILED: "onboarding_save_failed",
  ONBOARDING_ROLE_SELECTED: "onboarding_role_selected",
  ONBOARDING_COMPANY_SUBMITTED: "onboarding_company_submitted",
  ONBOARDING_MEETING_AGENT_CONFIGURED: "onboarding_meeting_agent_configured",
  /**
   * BRE-6985 (BRE-6312's measure) - the guided session's first goals are on
   * screen: fired once per conversation, when the goals card is placed in the
   * transcript, which waits for the writing turn to settle. So the elapsed time
   * includes the rest of that turn. Carries `via_import` (true when this conversation's
   * import run had created rows, so interview and import timings can be split;
   * false can include an import whose run the client had not loaded yet),
   * `goal_count`, `key_result_count`, `user_turns` and
   * `seconds_since_wizard_completed` (null when the wizard ran in another
   * browser - never estimated).
   */
  ONBOARDING_FIRST_GOAL_SAVED: "onboarding_first_goal_saved",
  // BRE-6993: a skipped connect step's post-interview reminder row was closed.
  ONBOARDING_CONNECT_REMINDER_CLOSED: "onboarding_connect_reminder_closed",
  ONBOARDING_GOAL_IMPORT_SHOWN: "onboarding_goal_import_shown",
  ONBOARDING_GOAL_IMPORT_CLICKED: "onboarding_goal_import_clicked",
  ONBOARDING_GOAL_IMPORT_DISMISSED: "onboarding_goal_import_dismissed",
  /**
   * BRE-5497: the missing end of the goal-import funnel that starts at
   * ONBOARDING_GOAL_IMPORT_SHOWN (the PMF readout counted the import UI shown
   * 10-15x per user with zero measurable completions). Fires exactly once per
   * completed import, from every path that can complete:
   *   - v1 concierge wizard (file + contact submitted; goals are created
   *     later by humans, so `goal_count` is null)
   *   - v2 wizard (execute+verify succeeded)
   *   - v3 agent flow (captured server-side when commitDrafts creates goals)
   *   - v4 conversational import (tree phase reached "live")
   * Properties: `import_version` ("v1_concierge" | "v2_wizard" | "v3_agent" |
   * "v4_conversational") and `goal_count` (goals/rows created; null when
   * unknowable client-side). Note: the older GOAL_IMPORT_COMPLETED constant
   * below is declared but captured nowhere - this event is the funnel-aligned
   * one to dashboard on.
   */
  /**
   * BRE-5598: NOTHING EMITS THIS ANY MORE. Its only emitter was
   * `trackGoalImportCompleted`, whose last call site (the conversational
   * import's session runner) was deleted when the goal-import wizards were
   * retired — the Operator handles import now and does not report completion
   * as a distinct client event. Historical data under this name is intact, so
   * dashboards still render; they are simply flat from that deploy onward. The
   * name is kept so a future import-completion signal can reuse it rather than
   * fragmenting the series. If you are looking at a zero here, this is why.
   */
  ONBOARDING_GOAL_IMPORT_COMPLETED: "onboarding_goal_import_completed",
  ONBOARDING_OBJECTIVES_SUGGESTED: "onboarding_objectives_suggested",
  ONBOARDING_OBJECTIVES_STREAMING_PHASE:
    "onboarding_objectives_streaming_phase",

  // Onboarding — About You step
  ONBOARDING_FIELD_SELECTED: "onboarding_field_selected",
  ONBOARDING_PROFILE_PICTURE_UPLOADED: "onboarding_profile_picture_uploaded",
  ONBOARDING_PROFILE_PICTURE_UPLOAD_FAILED:
    "onboarding_profile_picture_upload_failed",
  ONBOARDING_PROFILE_PICTURE_REMOVED: "onboarding_profile_picture_removed",

  // Onboarding — Calendar step
  ONBOARDING_CALENDAR_CONNECT_CLICKED: "onboarding_calendar_connect_clicked",
  ONBOARDING_CALENDAR_ALREADY_CONNECTED:
    "onboarding_calendar_already_connected",
  ONBOARDING_CALENDAR_CONNECT_SUCCEEDED:
    "onboarding_calendar_connect_succeeded",
  ONBOARDING_CALENDAR_CONNECT_FAILED: "onboarding_calendar_connect_failed",
  ONBOARDING_CALENDAR_SKIPPED: "onboarding_calendar_skipped",

  // Onboarding - Add Brev Coworker step (BRE-7006/BRE-7016, wizard-goal-interview
  // cohort). `provider` is "slack" or "teams" where one is known.
  ONBOARDING_WORKSPACE_CONNECT_CLICKED: "onboarding_workspace_connect_clicked",
  /** BRE-7565: no producer since the onboarding wizard's org-app branch was
   *  deleted. Kept so historical insights keyed on it still resolve. */
  ONBOARDING_WORKSPACE_ALREADY_CONNECTED: "onboarding_workspace_already_connected",
  ONBOARDING_WORKSPACE_CONNECT_SUCCEEDED: "onboarding_workspace_connect_succeeded",
  ONBOARDING_WORKSPACE_CONNECT_FAILED: "onboarding_workspace_connect_failed",
  ONBOARDING_WORKSPACE_SKIPPED: "onboarding_workspace_skipped",

  // BRE-8127: Connect Slack on the coworker row, either surface (`surface`).
  // The rest of the attempt is captured server-side: `ai_coworker.connect_started`
  // (sent to Slack's consent screen), then `_succeeded`, `_failed` or `_cancelled`.
  AI_COWORKER_CONNECT_CLICKED: "ai_coworker.connect_clicked",

  // Onboarding — Company step / Firmographics
  ONBOARDING_FIRMOGRAPHICS_RECEIVED: "onboarding_firmographics_received",
  ONBOARDING_FIRMOGRAPHICS_POLL_TIMEOUT:
    "onboarding_firmographics_poll_timeout",

  // Onboarding - Slack-first experiment (BRE-6847). `redirected` (once per
  // user), `returned` (once per callback landing, with `status` and
  // `oauth_error`) and `skipped` (a granted org kept on the web wizard, with
  // `reason`) are captured server-side. The clicks carry `from_state`.
  ONBOARDING_SLACK_FIRST_REDIRECTED: "onboarding_slack_first_redirected",
  ONBOARDING_SLACK_FIRST_RETURNED: "onboarding_slack_first_returned",
  ONBOARDING_SLACK_FIRST_SKIPPED: "onboarding_slack_first_skipped",
  ONBOARDING_SLACK_FIRST_CONTINUE_IN_SLACK_CLICKED:
    "onboarding_slack_first_continue_in_slack_clicked",
  ONBOARDING_SLACK_FIRST_CONTINUE_ON_WEB_CLICKED:
    "onboarding_slack_first_continue_on_web_clicked",

  // Onboarding — Error boundary
  ONBOARDING_ERROR_BOUNDARY_HIT: "onboarding_error_boundary_hit",

  // Onboarding — navigation watchdog (BRE-4492). The final step soft-navigates
  // into the RxDB-gated app; if that navigation never visually commits (the
  // destination's cold RxDB init / replication gates stall on a high-latency
  // connection), the user is stranded on a full-page spinner with no escape but
  // a manual refresh. Fires when the watchdog gives up and forces a hard reload
  // to recover — its rate is the missing signal for how often that hang occurs.
  ONBOARDING_NAV_WATCHDOG_FIRED: "onboarding_nav_watchdog_fired",

  // Stale-bundle chunk-load failure caught by RouteErrorBoundary —
  // tracks how often users hit a deploy-rotated webpack chunk so we can
  // size the impact window after each release.
  CHUNK_LOAD_ERROR: "chunk_load_error",

  // Stale-bundle Server Action version skew caught by RouteErrorBoundary
  // — a tab opened before a deploy invokes a server action whose id
  // rotated in the new build ("Failed to find Server Action … older or
  // newer deployment"). Same root cause as CHUNK_LOAD_ERROR; tracked
  // separately so the two recovery paths stay distinguishable.
  SERVER_ACTION_VERSION_SKEW: "server_action_version_skew",

  // Stale-bundle React #130 ("element type is invalid") caught by
  // RouteErrorBoundary — a deploy rotated the chunk hashes and a component
  // module now resolves to `undefined`, so React throws #130 at render.
  // Same stale-bundle root cause as CHUNK_LOAD_ERROR and recovered the same
  // way (reload once); tracked separately so we can tell a transient
  // deploy-window blip (recovers on reload) from a genuine undefined-component
  // bug (recurs after the reload). (BRE-4413.)
  ELEMENT_TYPE_INVALID_ERROR: "element_type_invalid_error",

  // ── Version update (soft-refresh) toast (BRE-4422) ────────────────────
  // Lifecycle of the <VersionUpdateToast> that replaces the legacy forced
  // reload. `variant` is "passive" (background skew detected) or "blocking"
  // (a user-triggered action failed). `toast_shown` carries an optional
  // `replacement` (the renamed action's successor) so operators can see which
  // renamed actions catch old tabs.
  //
  // Health signal: `soft_refresh_settled` with subsequent_failure=true means a
  // passive router.refresh() did NOT clear the skew (the action was genuinely
  // renamed and the user needs a full reload) — watch this rate to know whether
  // the soft path is actually fixing things. `fallback_reload` counts the
  // hard-reload fallbacks taken when router.refresh() threw outright.
  VERSION_UPDATE_TOAST_SHOWN: "version_update.toast_shown",
  // Passive "Update" clicked → router.refresh() fired. NOT a confirmed success:
  // router.refresh() returns void, so we can't await real completion. Named
  // "clicked_passive" (not "succeeded") so the event name doesn't lie.
  VERSION_UPDATE_UPDATE_CLICKED_PASSIVE:
    "version_update.update_clicked_passive",
  // Blocking "Refresh now" clicked → full window.location.reload().
  VERSION_UPDATE_UPDATE_CLICKED: "version_update.update_clicked",
  // Fired ~10s after a passive Update click. `subsequent_failure` is true if
  // staleActionFailureCount rose since the click — i.e. the soft refresh did
  // not actually resolve the skew.
  VERSION_UPDATE_SOFT_REFRESH_SETTLED: "version_update.soft_refresh_settled",
  VERSION_UPDATE_FALLBACK_RELOAD: "version_update.fallback_reload",

  // Frontend fetch failure with full request context (URL, method,
  // request id) — replaces the unactionable "Load failed" $exception
  // events that carry no fetch metadata.
  FETCH_FAILED: "fetch_failed",

  // Caught client render error with the React *component stack* attached
  // (BRE-4415). Next's `error.tsx` only gets `{ error, reset }`, so the
  // component stack — the one datum that names the subtree behind a #130
  // "element type is invalid … got undefined" — is otherwise lost in prod.
  // Emitted by DiagnosticErrorBoundary, which logs then re-throws so the
  // route error UI is unchanged.
  CLIENT_ERROR_DIAGNOSTIC: "client_error_diagnostic",

  // ── Goal AI Generation ────────────────────────────────────────────────
  GOAL_AI_GENERATION_STARTED: "goal_ai_generation_started",
  GOAL_AI_GENERATION_COMPLETED: "goal_ai_generation_completed",
  GOAL_AI_GENERATION_FAILED: "goal_ai_generation_failed",
  /** BRE-9468: a model's reading of a goal title filled metric fields the parser did not; once per answer. */
  GOAL_METRIC_TITLE_MODEL_FILLED: "goal_metric_title_model_filled",
  /**
   * Per-item event emitted once for each objective or KR created during a
   * generation run. Enables dashboards to count/time individual items
   * (e.g. 3 objectives + 9 KRs = 12 item events per onboarding run)
   * rather than collapsing the whole run into a single data point.
   */
  GOAL_AI_GENERATION_ITEM_COMPLETED: "goal_ai_generation_item_completed",
  /**
   * BRE-5497: explicit per-suggestion keep/reject signals for AI-generated
   * goal suggestions - previously inferred from keeps-vs-generations counts.
   * `_accepted` fires when the user explicitly keeps a suggestion (applies a
   * dialog suggestion card to the form, or accepts a temporary goal into the
   * goal list); `_dismissed` fires when the user explicitly rejects one
   * (rejects a temporary goal, regenerates over a dialog suggestion, or
   * cancels the suggestion cards). Auto-created onboarding objectives
   * (onboardingObjectiveStreamer) have no per-suggestion decision point, so
   * they emit generation events only. Properties: `surface` ("goal_dialog" |
   * "temporary_goal_list"), `via` on dismissed ("reject" | "regenerate" |
   * "cancel"), plus optional `suggestion_uuid`, `goal_type`, and `count`
   * (cancel dismisses every visible card in one action).
   */
  GOAL_AI_SUGGESTION_ACCEPTED: "goal_ai_suggestion_accepted",
  GOAL_AI_SUGGESTION_DISMISSED: "goal_ai_suggestion_dismissed",

  // ── Goals ─────────────────────────────────────────────────────────────
  GOAL_CREATED: "goal_created",
  GOAL_UPDATED: "goal_updated",
  GOAL_DELETED: "goal_deleted",
  GOAL_ARCHIVED: "goal_archived",
  GOAL_UNARCHIVED: "goal_unarchived",
  GOAL_STATUS_CHANGED: "goal_status_changed",
  /**
   * Outcome event: a goal transitioned into COMPLETED from any other status.
   * Fired from every path that can complete a goal (profile status selector,
   * quick check-in reaching 100%, check-in dialog) so effectiveness dashboards
   * can count real goal outcomes instead of proxies.
   */
  GOAL_ACHIEVED: "goal_achieved",
  GOAL_IMPORT_STARTED: "goal_import_started",
  GOAL_IMPORT_COMPLETED: "goal_import_completed",
  /** A goal was dragged to a new position among its siblings. `container`: "parent" | "top". */
  GOAL_REORDERED: "goal_reordered",

  // ── Check-ins ─────────────────────────────────────────────────────────
  CHECKIN_CREATED: "checkin_created",
  CHECKIN_DELETED: "checkin_deleted",
  /**
   * Check-in nudge funnel (BRE-4645). Dot-namespaced to line up with the
   * server-side `checkin.email_sent` event (CheckinReminderService) so
   * email / web / Slack read as one funnel in PostHog:
   * checkin.email_sent → checkin.email_clicked → checkin.started →
   * checkin.completed. Every client step carries a `source` property
   * ("email" | "web" today; the Slack-modal ticket adds "slack") —
   * keep names and the `source` prop aligned across surfaces.
   */
  CHECKIN_EMAIL_CLICKED: "checkin.email_clicked",
  CHECKIN_STARTED: "checkin.started",
  CHECKIN_COMPLETED: "checkin.completed",

  // ── Comments ──────────────────────────────────────────────────────────
  COMMENT_ADDED: "comment_added",

  // ── Actions (tasks / followups) ──────────────────────────────────────
  ACTION_CREATED: "action_created",
  ACTION_UPDATED: "action_updated",
  ACTION_STATUS_CHANGED: "action_status_changed",
  ACTION_ASSIGNED: "action_assigned",
  ACTION_DELETED: "action_deleted",
  /**
   * AI-suggested action funnel (BRE-4757). Brev generates pending "action
   * items" in the Action-items panel (e.g. on a cadence event, under "Generated
   * by Brev"). Until now nothing was tracked when a user kept/dismissed one, so
   * adoption of the suggest → keep → assign flow — the path that lands an action
   * in a user's Actions tab — was invisible. These make it a real funnel:
   *   suggested_action.shown → suggested_action.approved → (assignment)
   *                          ↘ suggested_action.dismissed
   * `_shown` fires once per distinct action uuid per list mount (deduped).
   * Every event carries `surface` ("cadence" | "goal_profile" | "other",
   * derived from CompactActionList's context props), `action_uuid`, and for
   * approve/dismiss a `bulk` flag + `count` (Keep all / Dismiss all). The
   * subsequent assignment is captured by ACTION_ASSIGNED, which now carries a
   * `source` so cadence assignments are distinguishable from Actions-page ones.
   */
  SUGGESTED_ACTION_SHOWN: "suggested_action.shown",
  SUGGESTED_ACTION_APPROVED: "suggested_action.approved",
  SUGGESTED_ACTION_DISMISSED: "suggested_action.dismissed",

  // ── Tags ──────────────────────────────────────────────────────────────
  TAG_CREATED: "tag_created",
  TAG_UPDATED: "tag_updated",
  TAG_DELETED: "tag_deleted",
  GOAL_TAGS_CHANGED: "goal_tags_changed",

  // ── Time Periods ─────────────────────────────────────────────────────
  TIME_PERIOD_CREATED: "time_period_created",
  TIME_PERIOD_UPDATED: "time_period_updated",
  TIME_PERIOD_DELETED: "time_period_deleted",

  // ── Inbox ─────────────────────────────────────────────────────────────
  INBOX_ITEM_READ: "inbox_item_read",
  INBOX_ALL_MARKED_READ: "inbox_all_marked_read",
  INBOX_ITEM_ARCHIVED: "inbox_item_archived",
  INBOX_BULK_ARCHIVED: "inbox_bulk_archived",

  // ── Organization membership ──────────────────────────────────────────
  ORG_MEMBER_ROLE_CHANGED: "org_member_role_changed",
  ORG_MEMBER_STATUS_CHANGED: "org_member_status_changed",
  ORG_MEMBER_INVITATION_SENT: "org_member_invitation_sent",
  ORG_MEMBER_BULK_INVITATIONS_SENT: "org_member_bulk_invitations_sent",
  ORG_INVITATION_DELETED: "org_invitation_deleted",

  // ── Goal interactions ────────────────────────────────────────────────
  GOAL_LINK_COPIED: "goal_link_copied",
  GOAL_KPI_CARD_TOGGLED: "goal_kpi_card_toggled",
  GOAL_PARENT_CHANGED: "goal_parent_changed",
  GOAL_OWNERS_CHANGED: "goal_owners_changed",
  GOAL_KR_SUGGESTIONS_REQUESTED: "goal_kr_suggestions_requested",

  // ── Cadences & Meetings ───────────────────────────────────────────────
  CADENCE_CREATED: "cadence_created",
  CADENCE_DELETED: "cadence_deleted",
  CADENCE_EVENT_VIEWED: "cadence_event_viewed",
  MEETING_NOTETAKER_TOGGLED: "meeting_notetaker_toggled",
  MEETING_HOST_ONLY_TOGGLED: "meeting_host_only_toggled",
  MEETING_SUMMARY_COPIED: "meeting_summary_copied",
  /**
   * Outcome event: a generated meeting summary was actually rendered to a
   * viewer (once per event per mount). Copy events undercount usage — most
   * readers never copy — so dashboards use this as the "summary was useful"
   * denominator.
   */
  MEETING_SUMMARY_VIEWED: "meeting_summary_viewed",
  MEETING_TRANSCRIPT_COPIED: "meeting_transcript_copied",
  MEETING_TRANSCRIPT_ENTRY_COPIED: "meeting_transcript_entry_copied",
  /**
   * BRE-8132: a join reminder that was on screen left it for good. Fired once
   * per viewer per meeting, by the one tab showing it. Properties: `outcome`
   * ("joined" | "closed" | "expired" | "ended"), `phase_at_resolution`
   * ("starting" | "live"), `visible_ms` (countdown time spent, i.e. visible
   * and unattended). Tunes JOIN_REMINDER_AUTO_DISMISS_MS.
   */
  MEETING_REMINDER_RESOLVED: "meeting_reminder_resolved",

  // ── Integrations ──────────────────────────────────────────────────────
  INTEGRATION_CONNECT_STARTED: "integration_connect_started",
  INTEGRATION_CONNECTED: "integration_connected",
  INTEGRATION_DISCONNECTED: "integration_disconnected",
  /**
   * OAuth callback came back with `oauth_error`. `source` names the surface
   * that rendered the failure: "settings" (Integrations page), or from the
   * dialogs bridge "onboarding" (guided session, BRE-6727), "goal_dialog"
   * (connect started from the goal dialog) or "app" (any other page).
   * `reason` is the `oauth_error` code. The failed event's `source` is
   * coarser than INTEGRATION_CONNECT_STARTED's: "onboarding" covers both
   * "onboarding_essentials" and "onboarding_closing", because the bridge only
   * sees the stored return action, which is the same for both beats. A
   * started-vs-failed join must match on the prefix, not equality.
   */
  INTEGRATION_CONNECTION_FAILED: "integration_connection_failed",
  GOAL_INTEGRATION_LINKED: "goal_integration_linked",
  /**
   * BRE-8974: the goal integration connect dialog closed by something other
   * than its own buttons. `reason`: "escape", "pointer_outside", or
   * "unmounted_open" (gone with no close at all, e.g. its provider remounted);
   * plus `step` and `app_type`. At most one per dialog open.
   */
  INTEGRATION_DIALOG_DISMISSED: "integration_dialog_dismissed",

  // ── Teams ─────────────────────────────────────────────────────────────
  TEAM_CREATED: "team_created",
  TEAM_DELETED: "team_deleted",
  TEAM_MEMBER_ADDED: "team_member_added",

  // ── Views ─────────────────────────────────────────────────────────────
  CUSTOM_VIEW_CREATED: "custom_view_created",
  CUSTOM_VIEW_DELETED: "custom_view_deleted",
  CUSTOM_VIEW_RENAMED: "custom_view_renamed",
  CUSTOM_VIEW_FILTERS_UPDATED: "custom_view_filters_updated",
  /**
   * BRE-4581: fired once per ViewPageWrapper preset/team auto-heal run — the
   * ensurePresetView (getViewBySlug) server action that runs when the view is
   * missing from the local mirror. `duration_ms` + `outcome` size the server
   * round-trip (the suspected contributor to the stale-tab "loading loop" on
   * the Goals route); the firing RATE is the key signal — it should fall to
   * ~zero for warm navigations once the sync-gap fallback (selectViewsData in
   * view-live-queries.ts) keeps the view resolved from the local mirror instead
   * of triggering this heal.
   */
  VIEW_AUTOHEAL_ENSURE: "view_autoheal_ensure",

  // ── Agent / AI ────────────────────────────────────────────────────────
  AGENT_MESSAGE_SENT: "agent_message_sent",
  /**
   * BRE-6985 - the user answered the Operator's docked question card (BRE-6943),
   * on any conversation. `submit_mode` is `pick` | `options` | `together`;
   * counts split answered, typed and skipped. Card SUBMISSIONS only: a card
   * closed or overtaken by a typed reply emits nothing, so this counts
   * questions answered through the card, not questions asked. `question_ids`
   * lists only ids the model tagged.
   */
  OPERATOR_QUESTION_CARD_ANSWERED: "operator_question_card_answered",
  AGENT_CREATED: "agent_created",
  AGENT_RENAMED: "agent_renamed",
  AGENT_DELETED: "agent_deleted",
  AGENT_TOGGLED: "agent_toggled",
  /**
   * BRE-5367: Operator experience telemetry. Both names are OWNED by
   * `@brev-io/common/src/utils/operator-turn-telemetry` and re-exported here so
   * the registry stays the single index of what the app emits - the browser and
   * agent-service must spell them identically or the two halves of a turn never
   * join in PostHog, so they are imported rather than retyped.
   *
   * `OPERATOR_TURN_FIRST_RESPONSE` is emitted by the chat transport (see
   * `lib/agent-chat/turn-telemetry.ts`); `OPERATOR_TURN_COMPLETED` is emitted by
   * agent-service and appears here only so a reader of this file sees the whole
   * event pair.
   */
  OPERATOR_TURN_FIRST_RESPONSE:
    OPERATOR_TELEMETRY_EVENTS.TURN_FIRST_RESPONSE,
  OPERATOR_TURN_COMPLETED: OPERATOR_TELEMETRY_EVENTS.TURN_COMPLETED,
  OPERATOR_TURN_CONTENT_PRESENTED: OPERATOR_TELEMETRY_EVENTS.TURN_CONTENT_PRESENTED,
  OPERATOR_TURN_PRESENTATION_DIAGNOSTIC:
    OPERATOR_TELEMETRY_EVENTS.TURN_PRESENTATION_DIAGNOSTIC,
  OPERATOR_TURN_STALE_SEND_REMINT: OPERATOR_TELEMETRY_EVENTS.TURN_STALE_SEND_REMINT,
  AI_FEEDBACK_SUBMITTED: "ai_feedback_submitted",
  AI_SUMMARY_GENERATED: "ai_summary_generated",
  AI_SUMMARY_CLEARED: "ai_summary_cleared",

  // ── Artifact preview (render-from-stream) ─────────────────────────────
  /**
   * BRE-4647 follow-up: the streaming artifact-preview overlay committed, but
   * the committed summary never reached the commit version even after the
   * bounded stuck-committed refetch backstop exhausted all its attempts
   * (`PREVIEW_COMMITTED_MAX_REFETCH_ATTEMPTS`). This distinguishes a genuine
   * backend divergence (the committed summary really never converges) from the
   * normal "rare race, self-heals within a few attempts" path — which fires
   * NOTHING. Without it, a never-converging preview is silently
   * indistinguishable from "still catching up" forever. Properties:
   * `artifact_uuid`, `tool_call_id`, `attempts_exhausted`, `elapsed_ms`
   * (time from commit to giving up).
   */
  ARTIFACT_PREVIEW_SUMMARY_STUCK: "artifact_preview_summary_stuck",

  // ── Agent workflow control ───────────────────────────────────────────
  WORKFLOW_STEP_SKIPPED: "workflow_step_skipped",
  WORKFLOW_STEP_RETRIED: "workflow_step_retried",
  WORKFLOW_INVITATION_ACCEPTED: "workflow_invitation_accepted",

  /**
   * BRE-5088: result of the `credentials: omit` A/B probe run when the auth
   * query fails. Its `verdict` is the one signal that distinguishes "the
   * browser's cookie jar is being rejected at the edge" from "something else
   * is". Fires only on the failure path, at most once per page load.
   */
  EDGE_REJECTION_PROBED: "edge_rejection_probed",

  // ── Performance ────────────────────────────────────────────────────────
  LOADING_PAGE_SHOWN: "loading_page_shown",
  LAYOUT_PREFETCH_DURATION: "layout_prefetch_duration",
  /**
   * BRE-4235 PR 1: fired from (authenticated)/layout.tsx the moment
   * the non-blocking prefetches are dispatched (before any await).
   * Verifies the layout is no longer blocking on Promise.allSettled.
   */
  LAYOUT_PREFETCH_DISPATCHED: "layout_prefetch_dispatched",
  /**
   * BRE-4235 PR 1: fired from (authenticated)/layout.tsx after the
   * user prefetch resolves and just before the layout returns JSX.
   * `user_resolution_ms` is the elapsed time from layout start to
   * user availability — the new first-paint-blocking critical path.
   */
  LAYOUT_RENDER_UNBLOCKED: "layout_render_unblocked",
  /**
   * BRE-4229: per-mount decision for each "data-wins" gated surface
   * (skeletons, LoadingPage, "view not found" placeholders). Lets us
   * verify the incremental-render fix is taking the warm-cache path
   * in production. See useTrackIncrementalRender for the property shape.
   */
  INCREMENTAL_RENDER_DECISION: "incremental_render_decision",
  /**
   * BRE-4229: PersonalGoalsPageWrapper specifically. Fires when the
   * per-navigation server round-trip to getPersonalGoalsPageBySlug is
   * either skipped (sync path won) or fired (sync miss). Used to size
   * the actual savings from the local-first refactor.
   */
  PERSONAL_GOALS_PAGE_SERVER_FETCH: "personal_goals_page_server_fetch",
  /**
   * BRE-4346: one event per route navigation, fired once primary data
   * is on screen. Replaces the dark spot where warm-cache renders skip
   * LoadingPage (and therefore never fire `loading_page_shown`), so
   * 25-second slow loads on /views/[viewSlug], /my-actions, /my-checkins
   * could previously emit zero telemetry. `severity` bands the load
   * into ok / slow / very_slow / critical for alerts. See
   * route-interactive.ts for emit semantics and the kill-switch
   * feature flag.
   */
  ROUTE_INTERACTIVE: "route.interactive",
  /**
   * BRE-6044: one event per goal-list expand/collapse toggle, from click to
   * the flattened row list actually committing to the DOM. Follow-up to
   * BRE-6028 (which removed the IndexedDB write on toggle) - live
   * measurement after that fix still showed 360-880ms per toggle with zero
   * IndexedDB involvement, so this exists to verify future fixes to that
   * remaining render-path cost from logs instead of a live repro each time.
   * See goal-toggle-latency.ts for emit semantics.
   */
  GOAL_TOGGLE_LATENCY: "goal_toggle.latency",
  /**
   * BRE-7925: one event per /artifacts visit, when the grid first settles
   * into content. The baseline and the verification signal for the Artifacts
   * load-time work. See artifacts-grid-first-content.ts.
   */
  ARTIFACTS_GRID_FIRST_CONTENT: "artifacts.grid_first_content",
  /**
   * BRE-9944: one event per cadence event Artifact tab mount, when its list
   * first settles into content. See event-artifacts-tab-first-content.ts.
   */
  EVENT_ARTIFACTS_TAB_FIRST_CONTENT: "artifacts.event_tab_first_content",
  /** Sampled artifact relay round trip; see relay-latency.ts. */
  ARTIFACT_RELAY_LATENCY_SAMPLED: "artifact.relay.latency_sampled",
  /**
   * BRE-5657: one event per TanStack query failure reaching the global
   * QueryCache error handler. Until this existed, a query failing on every
   * navigation was visible only as a `console.error` on the affected machine -
   * which is exactly how a per-navigation server-action failure went unnoticed
   * and undiagnosable.
   *
   * Carries NO free text. `query` is the query key's first element only (the
   * query NAME, e.g. `aiSummaryVersions`) - never the remaining key elements,
   * which are entity uuids. `classification` is a closed set, and `error_name`
   * is a constructor name. The error MESSAGE is deliberately excluded: it is
   * attacker- and user-influenced and routinely carries record identifiers and
   * user-authored content, none of which belongs in analytics.
   */
  QUERY_FAILED: "query_failed",
  /**
   * BRE-8443: one non-RSC reply to a Server Action POST, the reply Next hides
   * behind "An unexpected response was received from the server." Status,
   * content type, paths (public link tokens redacted), action id and edge
   * headers only; no body, query string or cookie. Fires on failure only.
   */
  SERVER_ACTION_BAD_RESPONSE: "server_action_bad_response",
  /**
   * BRE-9699: a Server Action or RSC request got the function URL's
   * header-overflow 413 and the user was shown the too-many-cookies toast. At
   * most once per page load. Carries `request_kind`, `clearable_cookie_count`
   * and `remedy` (`clear` or `sign-out`); no cookie names or values.
   */
  HEADER_OVERFLOW_SURFACED: "header_overflow_surfaced",
  /**
   * BRE-9118: a stackless React hydration error on /login, /signup or /invite,
   * renamed from `$exception` so it does not page; React recovered and sign-in
   * continues. Carries `hydration_dom_fingerprint` (tag and attribute names).
   */
  AUTH_HYDRATION_MISMATCH_RECOVERED: "auth_hydration_mismatch_recovered",
  /** A web vital (LCP, FCP, INP, TTFB) went past its slow threshold. See `app/providers.tsx`. */
  PAGE_SLOW_LOAD: "page.slow_load",
  /** An RxDB replication stream errored. See `RxDBSyncProvider`. */
  RXDB_REPLICATION_FAILED: "rxdb.replication_failed",

  // ── Product Tours ──────────────────────────────────────────────────────
  TOUR_STARTED: "tour_started",
  TOUR_STEP_VIEWED: "tour_step_viewed",
  TOUR_STEP_COMPLETED: "tour_step_completed",
  TOUR_COMPLETED: "tour_completed",
  TOUR_SKIPPED: "tour_skipped",
  TOUR_STEP_SKIPPED: "tour_step_skipped",
  // BRE-6563: the launch gate parked because no goal can render the tour's
  // terminal step. The cookie survives, so this is a DEFERRED tour, not a lost
  // one - but without an event the deferred cohort is invisible, and "waiting"
  // and "never delivered" are the same silence.
  TOUR_LAUNCH_DEFERRED: "tour_launch_deferred",

  // ── Settings & Org ────────────────────────────────────────────────────
  SETTINGS_CHANGED: "settings_changed",
  /**
   * BRE-6625: an org admin asked for a feature they cannot switch on
   * themselves. This IS the demand signal behind the "By Request" placeholder —
   * the mailto beside it is how they reach us, not how we learn they wanted it,
   * since nobody can read intent out of an inbox.
   */
  FEATURE_REQUESTED: "feature_requested",
  /**
   * `source` is typed as {@link InviteSource}, whose union IS the taxonomy.
   * Add a value there when adding an invite surface, or the BRE-5497 funnel
   * silently splits into a new bucket.
   */
  INVITE_SENT: "invite_sent",
  /**
   * BRE-5497: splits "0 invites" into "nobody saw the invite UI" vs "everyone
   * declined". A bucket appearing or vanishing at a deploy is a SURFACE change,
   * not a conversion one (BRE-6405 retired `"tour"`, BRE-6593 added
   * `"nav_sidebar"`).
   *
   * `nav_sidebar`'s rate is NOT comparable to the others': they are dialogs
   * (one `_shown` = one deliberate arrival), while the nav row is ambient and
   * reports every VISIBLE APPEARANCE - re-firing on return from `/settings` or
   * `/docs`, or on reopening a collapsed sidebar. Compare it against sessions.
   */
  INVITE_UI_SHOWN: "invite_ui_shown",
  INVITE_UI_OPENED: "invite_ui_opened",

  // ── Navigation ────────────────────────────────────────────────────────
  /**
   * Fires once per mount of a top-level authenticated surface. `page_group`
   * is a closed enum for PostHog funnels; `page` is a dotted path for
   * fine-grained filtering (e.g. "settings.integrations"). Additional
   * properties (view_slug, goal_uuid, cadence_uuid) may be attached when
   * the route carries a contextual id — never include user-authored names.
   */
  PAGE_VIEWED: "page_viewed",

  // ── Mobile ────────────────────────────────────────────────────────────
  /**
   * Fired once per route mount when the user is on a mobile viewport. Used
   * to track adoption and surface regressions per-route.
   */
  MOBILE_LAYOUTS_ROUTE_VIEWED: "mobile_layouts.route_viewed",

  // ── Command Palette ───────────────────────────────────────────────────
  COMMAND_PALETTE_OPENED: "command_palette.opened",
  COMMAND_PALETTE_USER_SELECTED: "command_palette.user_selected",

  // ── Docs (BRE-8375) ───────────────────────────────────────────────────
  /**
   * "Was this page helpful?" answered on a /docs page. At most once per page
   * view: the control locks after the first answer. Properties: `doc_slug`,
   * `helpful` (boolean).
   */
  DOCS_PAGE_FEEDBACK: "docs_page_feedback",
  /**
   * BRE-8856: after a "No", the reader picked what went wrong and sent it. At
   * most once per page view, after `docs_page_feedback`. Properties:
   * `doc_slug`, `reason` (closed enum, `DOC_FEEDBACK_REASONS`), `comment`
   * (optional free text, capped at 1000 characters, from anonymous readers too).
   */
  DOCS_PAGE_FEEDBACK_DETAIL: "docs_page_feedback_detail",

  // ── Home (BRE-4669) ───────────────────────────────────────────────────
  /**
   * Fired once per Home visit after the nudge feed finishes loading, with the
   * feed composition (`nudge_count`, `is_empty`, and per-kind counts as
   * `count_<kind>`). Lets us see what the feed actually surfaces for users.
   */
  HOME_VIEWED: "home.viewed",
  /**
   * A nudge card's action was clicked. Properties: `nudge_kind`, `action_label`,
   * `position` (0-based rank in the feed), `is_external`. No user-authored text.
   */
  HOME_NUDGE_CLICKED: "home.nudge_clicked",
  /** The Home "Ask Brev" bar launched the agent. Property: `query_length`. */
  HOME_AGENT_LAUNCHED: "home.agent_launched",
  /** A right-rail suggestion's action was clicked. Property: `suggestion`. */
  HOME_SUGGESTION_CLICKED: "home.suggestion_clicked",
  /** A right-rail suggestion was dismissed. Property: `suggestion`. */
  HOME_SUGGESTION_DISMISSED: "home.suggestion_dismissed"
} as const);

/** Union type of all tracked event names. */
export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
